import { FC, useState } from 'react'
import { Box, ClickAwayListener, Link as MuiLink, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import theme from 'theme'

/* Utils */
import { linkSx } from '../../pages/dashboard/sections/category-activity/components/DataSourceErrorTooltip'
import { getIntegrationLogo } from 'lib/utils'

/* Components */
import { ErrorTooltip } from './ErrorTooltip'
import { ErrorIcon } from '../svg'
import {
  dataSourceErrorBackground,
  dataSourceErrorIcon
} from '../../pages/dashboard/sections/category-activity/components/dataSourceErrorIcons'
import { Container } from './dataSourceErrorTooltip.styles'

interface Props {
  logoPath: string
  instanceName: string
  id: string
  logoSize?: number
  chip?: boolean
  cpiName?: string
  useSquareLogo?: boolean
  hideBaseIcon?: boolean
  hideLink?: boolean
  integrationName?: string
  isTrendsError?: boolean
  shouldDisablePortal?: boolean
  isCpiPage?: boolean
  imgStyle?: Record<string, unknown>
}

const DataSourceErrorTooltip: FC<Props> = ({
  logoPath,
  logoSize = 18,
  instanceName,
  id,
  chip,
  cpiName,
  useSquareLogo,
  hideBaseIcon,
  hideLink = false,
  integrationName,
  isTrendsError,
  shouldDisablePortal = true,
  isCpiPage,
  imgStyle
}) => {
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)

  const handleTooltipClose = () => {
    setOpen(false)
    setFocused(false)
  }

  const handleIconClick = () => {
    setOpen(true)
    setFocused(true)
  }

  const getLogo = () => {
    if (useSquareLogo) {
      return `/icons/rounded/${logoPath}`
    }

    return `/icons/circle/${logoPath}`
  }

  const cpiLogo = `${getIntegrationLogo(logoPath, 'circle').replace('_icon.svg_icon.svg', '_icon.svg')}`

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Container logoSize={logoSize} isSquare={useSquareLogo} className="data-source-error-tooltip-container">
        <ErrorTooltip
          placement="bottom-start"
          componentsProps={{
            popper: {
              sx: {
                borderRadius: `${theme.attributes.borderRadius.secondary} !important`,
                background: '#342F32 !important',
                marginTop: '14px !important',
                zIndex: 100000000,
                "html[data-theme='light'] &": {
                  background: `${theme.colors.tooltipBg} !important`
                }
              }
            }
          }}
          PopperProps={{
            disablePortal: shouldDisablePortal
          }}
          onClose={handleTooltipClose}
          open={open || focused}
          isChip={!!chip}
          isSquare={useSquareLogo}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          title={
            <>
              <Box className="tooltip-wrapper">
                <Box className="instance-name-container">
                  <img
                    src={
                      isCpiPage
                        ? cpiLogo
                        : integrationName
                        ? getIntegrationLogo(integrationName, 'circle')
                        : chip
                        ? logoPath
                        : `/icons/circle/${logoPath}`
                    }
                    alt=""
                    width="24px !important"
                    height="24px !important"
                    style={{ minWidth: 'unset !important' }}
                  />
                  <Typography className="instance-name">{instanceName}</Typography>
                </Box>
                <Box className="error-description-container">
                  <Box className="error-icon">
                    <ErrorIcon />
                  </Box>
                  <Typography className="error-text">Data Synchronization Failure</Typography>
                </Box>
                {!hideLink && (
                  <MuiLink
                    component={Link}
                    color="inherit"
                    to={'/settings/integrations/data-sources'}
                    sx={linkSx}
                    state={{ instanceId: id }}
                  >
                    Adjust Configuration
                  </MuiLink>
                )}
              </Box>
            </>
          }
        >
          {chip ? (
            <Box
              className="cpi-chip"
              onClick={handleIconClick}
              onMouseEnter={() => {
                setOpen(true)
              }}
              onMouseLeave={() => {
                setOpen(focused)
              }}
            >
              {cpiName || ''}
              {dataSourceErrorBackground}
              {dataSourceErrorIcon}
            </Box>
          ) : (
            <Box
              className={`error-wrapper ${hideBaseIcon ? 'no-base-icon' : ''}${
                isTrendsError ? 'trends-chart-error' : ''
              }`}
              onClick={handleIconClick}
              onMouseEnter={() => {
                setOpen(true)
              }}
              onMouseLeave={() => {
                setOpen(focused)
              }}
            >
              {!hideBaseIcon && <img src={getLogo()} alt="" width={logoSize} height={logoSize} style={imgStyle} />}
              {dataSourceErrorBackground}
              {dataSourceErrorIcon}
            </Box>
          )}
        </ErrorTooltip>
      </Container>
    </ClickAwayListener>
  )
}

export default DataSourceErrorTooltip
